<template>
    <div>
        <div v-if="!noTitle" class="title-section">
            {{ $t('common.panel_daily') }}
        </div>
        <div class="table-card" :class="{ 'with-actions': !studentId }">
            <kimsa-grid-table :headers="headers" :loading="loading" :items="students" item-key="id"
                sort-by="status" :disable-sort="!!studentId" :no-data-text="$t('common.not_found')">
                <template v-slot:item.img="{ item }">
                    <div class="avatar-wrap">
                        <div v-if="item.avatar" class="student-avatar" :style="{ backgroundImage: `url(${item.avatar})` }" />
                        <div v-else class="student-avatar-initials" :style="item.avatarGradient">{{ item.initials }}</div>
                        <div v-if="item.hasAiAlert" class="ai-alert-badge">
                            <v-icon size="13" color="white">mdi-robot-excited-outline</v-icon>
                        </div>
                        <div v-if="item.hasInjury" class="injury-badge">
                            <v-icon size="20" color="white">$injury</v-icon>
                        </div>
                    </div>
                </template>
                <template v-slot:item.name="{ item }">
                    <truncated-text :text="item.name" />
                </template>
                <template v-slot:item.week="{ item }">
                    <routine-week-dots :week="item.week" :loading="loadingWeeks" />
                </template>
                <template v-slot:item.month="{ item }">
                    <span class="month-score">{{ item.month }}</span>
                </template>
                <template v-slot:item.lastWellness="{ item }">
                    <wellness-dots :wellness="item.lastWellness" @click:dot="(w) => onDotClick(item, w)" />
                </template>
                <template v-slot:item.actions="{ item }">
                    <div class="row-actions">
                        <button-tooltip left>
                            <template v-slot:activator="{ on, attrs }">
                                <button class="icon-btn" :class="{ 'has-note': item.hasNote }" v-bind="attrs" v-on="on"
                                    @click="showNoteCoachDialog(item)">
                                    <v-icon size="15">{{ item.hasNote ? 'mdi-note-edit' : 'mdi-note-plus' }}</v-icon>
                                </button>
                            </template>
                            <template v-slot:tooltip><span v-html="formatMessage(item)" /></template>
                        </button-tooltip>
                        <button-tooltip left :tooltip="$t('tooltips.see_statistics')">
                            <template v-slot:activator="{ on, attrs }">
                                <button class="icon-btn" v-bind="attrs" v-on="on"
                                    @click="toStudentStatistics(item.id)">
                                    <v-icon size="15">mdi-chart-box-outline</v-icon>
                                </button>
                            </template>
                        </button-tooltip>
                        <button-tooltip left :tooltip="$t('tooltips.see_recommendation')">
                            <template v-slot:activator="{ on, attrs }">
                                <button class="icon-btn" v-bind="attrs" v-on="on"
                                    @click="$refs.recommendationDialog.show(item.id)">
                                    <v-icon size="15">mdi-robot-outline</v-icon>
                                </button>
                            </template>
                        </button-tooltip>
                    </div>
                </template>
                <template v-slot:footer v-if="!studentId">
                    <div class="table-footer">
                        <div class="search-wrap">
                            <kimsa-text-field class="search-field" @change="search = $event" :value="search"
                                :placeholder="$t('common.search')" prepend-inner-icon="mdi-magnify" dense />
                        </div>
                        <button class="filter-btn" :class="{ active: !!filterStudents }" @click="showFilterStudents">
                            <v-icon size="18">mdi-filter-outline</v-icon>
                        </button>
                        <kimsa-pagination v-if="pageCount > 1" class="ml-auto pagination-wrap" height="auto"
                            @page="page = $event - 1" :pageCount="pageCount" :total-visible="isMobile ? 4 : 7" />
                    </div>
                </template>
            </kimsa-grid-table>
        </div>
        <notes-student-dialog ref="notes" @saved="onSaveNotes" />
        <wellness-activities-dialog ref="wellnessActivities" />
        <ai-recommendation-dialog ref="recommendationDialog" />
        <filter-students-dialog v-if="!studentId" :teams="teams" :groups="groups" ref="filter"
            @filter="onFilterStudents" />
    </div>
</template>

<script>
import { mapState } from "vuex";
import { UserIndex } from "@/utils/Algolia";
import KimsaPagination from "@/components/common/KimsaPagination";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import NotesStudentDialog from "@/components/coach/NotesStudentDialog";
import FilterStudentsDialog from "@/components/coach/FilterStudentsDialog";
import { debounce, getInitials, getAvatarGradient, nl2br } from "@/utils/Common";
import { buildWeekFromRoutines, emptyWeek, fetchAssignedRoutinesRange, studentWeekStart } from "@/utils/Routine";
import WellnessDots from './WellnessDots.vue';
import WellnessActivitiesDialog from './WellnessActivitiesDialog.vue';
import RoutineWeekDots from './RoutineWeekDots.vue';
import AiRecommendationDialog from '@/components/coach/ai/AiRecommendationDialog.vue';
import TruncatedText from '../common/TruncatedText.vue';
import ButtonTooltip from '../common/ButtonTooltip.vue';
import KimsaGridTable from '../common/KimsaGridTable.vue';

export default {
    name: "StudentProgressTable",
    components: {
        NotesStudentDialog, KimsaPagination, KimsaTextField, FilterStudentsDialog,
        ButtonTooltip, WellnessDots, WellnessActivitiesDialog, RoutineWeekDots, TruncatedText, KimsaGridTable,
        AiRecommendationDialog,
    },
    props: {
        coachId: {
            type: String,
            default: null,
        },
        studentId: {
            type: String,
            default: null,
        },
        noTitle: {
            type: Boolean,
            default: false,
        },
        alertStudentIds: {
            type: Array,
            default: () => [],
        },
    },
    data(vm = this) {
        return {
            students: [],
            loading: false,
            student: null,
            search: '',
            page: 0,
            pageCount: 0,
            perPage: 10,
            loadingWeeks: false,
            filterStudents: null,
        }
    },
    computed: {
        ...mapState(['groups', 'teams']),
        headers(vm = this) {
            let headers = [
                { text: '', value: 'img', sortable: false, width: '56px' },
                { text: vm.$t('common.name'), value: 'name', width: 'minmax(140px, 1fr)' },
                { text: vm.$t('common.week'), value: 'week', width: '210px', sortable: false },
                { text: vm.$t('common.month'), value: 'month', width: '74px', align: 'center' },
                { text: vm.$t('common.last_wellness'), value: 'lastWellness', width: '256px', sortable: false },
                { text: '', value: 'actions', sortable: false, width: '100px', stickyEnd: true },
            ]
            if (vm.studentId) return headers.filter(h => h.value !== 'actions')
            return headers
        },
    },
    async mounted() {
        const vm = this
        if (vm.coachId) await vm.loadStudents()
        if (vm.studentId) vm.loadStudentData()
    },
    methods: {
        async loadStudents() {
            const vm = this
            vm.loading = true
            vm.loadingWeeks = true
            try {
                vm.students = []

                let filter = `studentSubscription.coachId: ${vm.coachId} AND studentSubscription.paused != 1`
                if (vm.filterStudents) {
                    if (vm.filterStudents.group) filter += ` AND group:${vm.filterStudents.group}`
                    if (vm.filterStudents.team) filter += ` AND team.id:${vm.filterStudents.team}`
                    if (vm.filterStudents.subgroupId || vm.filterStudents.subgroupId >= 0) filter += ` AND team.subgroupId:${vm.filterStudents.subgroupId}`
                }

                let response = await UserIndex.search(vm.search, {
                    filters: filter,
                    hitsPerPage: vm.perPage,
                    page: vm.page,
                })

                vm.pageCount = response.nbPages || 0
                vm.students = response.hits.map(hit => ({
                    id: hit.objectID,
                    name: hit.name,
                    email: hit.email,
                    avatar: hit.avatar || null,
                    initials: getInitials(hit.name),
                    avatarGradient: getAvatarGradient(hit.objectID || hit.name),
                    week: emptyWeek(),
                    month: hit?.counter?.month || 0,
                    lastWellness: hit?.lastWellness || [],
                    hasNote: hit?.coachNote?.message || false,
                    coachNote: hit?.coachNote || null,
                    hasInjury: hit?.hasInjury || false,
                    hasAiAlert: vm.alertStudentIds.includes(hit.objectID),
                    iconWeekStatus: vm.getIconWeek(hit?.weekStatus || 'to_be_done'),
                }));

                vm.loading = false

                const routinesByStudent = await vm.fetchWeekRoutines(vm.students.map(s => s.id))
                vm.students.forEach(student => {
                    student.week = buildWeekFromRoutines(routinesByStudent[student.id] || [])
                })
            } catch (error) {
                vm.toast(vm.$t('common.error_generic'), 'error')
                throw error
            } finally {
                vm.loading = false
                vm.loadingWeeks = false
            }
        },
        async loadStudentData() {
            const vm = this
            await vm.withLoading(async () => {
                const routinesByStudent = await vm.fetchWeekRoutines([vm.studentId])
                vm.students = [{
                    id: vm.studentId,
                    name: vm.account?.name || '',
                    email: vm.account?.email || '',
                    avatar: vm.account?.avatar || null,
                    initials: getInitials(vm.account?.name),
                    avatarGradient: getAvatarGradient(vm.studentId || vm.account?.name),
                    week: buildWeekFromRoutines(routinesByStudent[vm.studentId] || []),
                    month: vm.account?.counter?.month || 0,
                    lastWellness: vm.account?.lastWellness || [],
                    hasInjury: vm.account?.hasInjury || false,
                    hasAiAlert: vm.alertStudentIds.includes(vm.studentId),
                    iconWeekStatus: vm.getIconWeek(vm.account?.weekStatus || 'to_be_done'),
                }]
            })
        },
        async fetchWeekRoutines(studentIds) {
            const start = studentWeekStart()
            return fetchAssignedRoutinesRange(
                studentIds,
                start.format('YYYY-MM-DD'),
                start.clone().add(6, 'days').format('YYYY-MM-DD')
            )
        },
        toStudentStatistics(studentId) {
            const vm = this
            vm.pushRoute({ name: 'coach.statistics', query: { studentId } })
        },
        showNoteCoachDialog(student) {
            const vm = this
            vm.$refs.notes.show(student)
        },
        onDotClick(student, wellnessItem) {
            const vm = this
            vm.$refs.wellnessActivities.show(student.id, wellnessItem.date, student.lastWellness || [])
        },
        onSaveNotes(studentId, notes) {
            const vm = this
            let index = vm.students.findIndex(student => student.id === studentId)
            if (index >= 0) {
                vm.students[index].hasNote = !!notes
                vm.students[index].coachNote = notes || null
            }
        },
        formatMessage(student) {
            const vm = this
            return nl2br(student.coachNote?.message || vm.$t('tooltips.add_note'))
        },
        getIconWeek(status) {
            if (status === 'finished') return { icon: 'mdi-check', color: '#4cb351' }
            if (status === 'incomplete') return { icon: 'mdi-pencil', color: '#ac7d1d' }
            if (status === 'to_be_done') return { icon: 'mdi-close', color: '#9a2d2d' }
            return { icon: '', color: '#000000' }
        },
        showFilterStudents() {
            const vm = this
            vm.$refs.filter.show()
        },
        onFilterStudents(filter = null) {
            const vm = this
            vm.filterStudents = filter
            vm.resetPageOrReload()
        },
        resetPageOrReload() {
            const vm = this
            if (vm.page === 0) vm.loadStudents()
            else vm.page = 0
        },
        debouncedSearch: debounce(function () {
            this.resetPageOrReload()
        }, 500),
    },
    watch: {
        coachId(val) {
            if (val) this.loadStudents()
        },
        page() {
            this.loadStudents()
        },
        search() {
            this.debouncedSearch()
        },
        alertStudentIds() {
            // Actualizar hasAiAlert en los estudiantes ya cargados cuando cambien las alertas
            const vm = this
            vm.students.forEach(student => {
                student.hasAiAlert = vm.alertStudentIds.includes(student.id)
            })
        },
    }
}
</script>

<style scoped>
.title-section {
    font-weight: 700;
    font-size: 20px;
    line-height: 22px;
    margin-bottom: 12px;
    color: var(--v-txtPrimary-base);
}

.table-card {
    min-width: 0;
}

.avatar-wrap {
    position: relative;
    width: 34px;
    height: 34px;
}

.student-avatar {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    background-size: cover;
    background-position: center;
    background-color: var(--v-surface2-base);
}

.student-avatar-initials {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.02em;
}

.injury-badge {
    position: absolute;
    bottom: -6px;
    right: -6px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--v-error-base);
    border: 2px solid var(--v-surface-base);
    display: flex;
    align-items: center;
    justify-content: center;
}

.ai-alert-badge {
    position: absolute;
    top: -6px;
    right: -6px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--v-accent-base);
    border: 2px solid var(--v-surface-base);
    display: flex;
    align-items: center;
    justify-content: center;
}

.month-score {
    font-size: 14px;
    font-weight: 500;
    color: var(--v-txtSecondary-base);
}

.row-actions {
    display: flex;
    gap: 5px;
    align-items: center;
    justify-content: flex-end;
    opacity: 0;
    transition: opacity 0.15s;
}

>>> .kgt-row:hover .row-actions {
    opacity: 1;
}

@media (min-width: 961px) {
    >>> .kgt-row:hover .kgt-sticky {
        background: linear-gradient(to right, transparent, var(--v-surface2-base) 28px);
    }
}

@media (max-width: 960px) {
    .row-actions {
        opacity: 1;
    }
}

.icon-btn {
    width: 26px;
    height: 26px;
    border-radius: 6px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline2-base);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--v-txtSecondary-base);
    transition: background 0.15s, color 0.15s;
}

.icon-btn:hover {
    background: var(--v-outline2-base);
    color: var(--v-txtPrimary-base);
}

.icon-btn.has-note {
    background: rgba(102, 204, 204, 0.15);
    border-color: var(--v-secondary-base);
    color: var(--v-secondary-base);
}

.table-footer {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    border-top: 1px solid var(--v-outline-base);
}

.search-wrap {
    flex: 1 1 120px;
    max-width: 280px;
}

.pagination-wrap {
    flex: 0 0 auto;
    min-width: 0;
}

.filter-btn {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline2-base);
    cursor: pointer;
    color: var(--v-txtSecondary-base);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s, color 0.15s;
    flex-shrink: 0;
}

.filter-btn:hover {
    background: var(--v-surface2-base);
    color: var(--v-txtPrimary-base);
}

.filter-btn.active {
    background: rgba(51, 153, 153, 0.12);
    border-color: var(--v-accent-base);
    color: var(--v-accent-base);
}
</style>
